import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  Stack,
  Typography,
} from "@mui/joy";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

interface LogoUploadProps {
  mode: "light" | "dark";
  currentUrl: string | null;
  onUpload: (file: File) => void | Promise<void>;
  onDelete: () => void | Promise<void>;
  disabled?: boolean;
}

const ACCEPTED = ".png,.jpg,.jpeg,.svg,.webp";

const LogoUpload: React.FC<LogoUploadProps> = ({
  mode,
  currentUrl,
  onUpload,
  onDelete,
  disabled,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      setError("File must be under 2 MB");
      return;
    }

    setError(null);

    try {
      await onUpload(file);
    } catch (e: any) {
      setError(e?.message ?? "Upload failed");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);

    try {
      await onDelete();
    } catch (e: any) {
      setError(e?.message ?? "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const bgColor = mode === "dark" ? "#1e293b" : "#f8fafc";
  const label = mode === "dark" ? "Dark Mode Logo" : "Light Mode Logo";

  return (
    <Box>
      <Typography level="title-sm" sx={{ mb: 1, fontWeight: 600 }}>
        {label}
      </Typography>

      {error && (
        <Alert color="danger" variant="soft" size="sm" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Box
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        sx={{
          border: "2px dashed",
          borderColor: "divider",
          borderRadius: "8px",
          p: 2,
          textAlign: "center",
          bgcolor: bgColor,
          cursor: disabled ? "not-allowed" : "pointer",
          transition: "border-color 0.2s",
          "&:hover": disabled ? {} : { borderColor: "primary.400" },
        }}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        {currentUrl ? (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
            <Box
              component="img"
              src={currentUrl}
              alt={label}
              sx={{ maxHeight: 48, maxWidth: "100%", objectFit: "contain" }}
            />
            <Typography level="body-xs" color="neutral">
              Click to replace
            </Typography>
          </Box>
        ) : (
          <Stack spacing={0.5} alignItems="center">
            <UploadIcon sx={{ fontSize: 28, color: "text.tertiary" }} />
            <Typography level="body-sm" color="neutral">
              Click or drag to upload
            </Typography>
            <Typography level="body-xs" color="neutral">
              PNG, JPG, SVG, WebP - max 2 MB
            </Typography>
          </Stack>
        )}
      </Box>

      {currentUrl && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
          <IconButton
            size="sm"
            variant="plain"
            color="danger"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete();
            }}
            loading={deleting}
            disabled={disabled || deleting}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED}
        style={{ display: "none" }}
        onChange={handleInputChange}
        disabled={disabled}
      />
    </Box>
  );
};

export default LogoUpload;
